// EN-1088 recording lease. Every call goes through apiFetch (impersonation + clinic headers). Silent: the
// lease is best-effort, so its caller decides what a failure means (nothing, for the recorder).
import { apiFetch } from "../api";

// EN-1448: `holder` names the recording session, so one recorder's release cannot end another's lease.
const path = (patientRecordId: string, holder: string) =>
    `v1/patient-records/${encodeURIComponent(patientRecordId)}/recording-lease?holder=${encodeURIComponent(holder)}`;

/**
 * Marks the visit as being recorded for the server's TTL (5 min) from now. Resolves with the kept visit's
 * id when the server refuses because this visit was merged into it (409 merged_visit; no lease is set),
 * else null. Any other failure rejects.
 */
export const renewRecordingLease = async (
    patientRecordId: string,
    holder: string,
): Promise<{ keptVisitId: string } | null> => {
    try {
        await apiFetch(path(patientRecordId, holder), { method: "POST", silent: true });
        return null;
    } catch (error) {
        const response = (error as { response?: Response } | null)?.response;
        if (response?.status === 409) {
            const body = (await response
                .clone()
                .json()
                .catch(() => null)) as { code?: unknown; keptVisitId?: unknown } | null;
            if (body?.code === "merged_visit" && typeof body.keptVisitId === "string") {
                return { keptVisitId: body.keptVisitId };
            }
        }
        throw error;
    }
};

/** keepalive lets the release outlive a closing page. */
export const releaseRecordingLease = async (
    patientRecordId: string,
    holder: string,
    keepalive = false,
): Promise<void> => {
    await apiFetch(path(patientRecordId, holder), { method: "DELETE", silent: true, keepalive });
};
